import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '../App';
import { analyzeAPI } from '../lib/api';
import { apiErrorMessage } from '../lib/apiErrors';
import { runJob } from '../lib/jobs';
import { t } from '../lib/i18n';
import { toast } from 'sonner';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { KpiGrid } from '../components/dashboard/KpiGrid';
import { QuickActions } from '../components/dashboard/QuickActions';
import { RecentSessions } from '../components/dashboard/RecentSessions';
import { ReadingCard } from '../components/dashboard/ReadingCard';

export default function DashboardPage() {
  const { stats, loading } = useDashboardStats();
  const [analyzing, setAnalyzing] = useState(false);
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleFullAnalysis = async () => {
    setAnalyzing(true);
    const pending = toast.loading(t('analysisInProgress', language));
    try {
      const result = await runJob(() => analyzeAPI.full(language));
      toast.dismiss(pending);
      toast.success(language === 'fr' ? 'Analyse compl\u00e8te g\u00e9n\u00e9r\u00e9e!' : 'Full analysis generated!');
      navigate(`/coach/${result.session_id}`);
    } catch (err) {
      toast.dismiss(pending);
      if (err.code === 'job_timeout') toast.error(t('jobTimeout', language));
      else toast.error(apiErrorMessage(err, language, language === 'fr' ? 'Erreur lors de la g\u00e9n\u00e9ration de l\'analyse' : 'Error generating analysis'));
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="dashboard-page">
      <div className="fade-in">
        <h1 className="font-display text-3xl lg:text-4xl text-white" data-testid="dashboard-greeting">
          {language === 'fr' ? `Bonjour, ${user?.name || ''}` : `Hello, ${user?.name || ''}`}
        </h1>
        <p className="text-white/50 text-sm mt-1">
          {language === 'fr' ? 'Votre tableau de bord personnel' : 'Your personal dashboard'}
        </p>
      </div>

      <KpiGrid stats={stats} language={language} />
      <QuickActions language={language} onNavigate={navigate} onFullAnalysis={handleFullAnalysis} analyzing={analyzing} />
      <ReadingCard latestProgress={stats?.latest_progress || []} language={language} />
      <RecentSessions
        sessions={stats?.recent_sessions || []}
        language={language}
        onOpen={(id) => navigate(`/coach/${id}`)}
        onViewAll={() => navigate('/history')}
      />
    </div>
  );
}
